import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Compass, Dumbbell, IdCard, Bot } from 'lucide-react';
import CVSearch from './CVSearch';
import CVUpload from './CVUpload';
import CandidateSearch from './CandidateSearch';
import Creators from './Creators';
import Community from './Community';
import HiringBoard from './HiringBoard';
import MockHome from './mock/MockHome';
import MyPassport from './MyPassport';
import JobRadarAIView from './JobRadarAIView';
import { useViewMode } from '../contexts/ViewModeContext';
import { useUserProfile } from '../contexts/UserProfileContext';
import RecruiterExplorerGate from '../components/RecruiterExplorerGate';
import { useWatchApi } from '../services/watchApi';
import { normalizeExploreView, getVisibleExploreViews, EXPLORE_COMMUNITY_ENABLED, EXPLORE_CREATORS_ENABLED, JOB_RADAR_ENABLED, type ExploreViewId } from '../config/features';

type TabType = 'explore' | 'prep' | 'practice' | 'passport' | 'inspire' | 'hire' | 'watch' | 'watch-legacy';

function normalizeExplorerTab(tab: string | null): TabType | null {
  if (!tab) return null;
  // Deprecated tabs → fall back so old links/bookmarks don't dead-end.
  if (tab === 'prep') return 'explore';
  if (tab === 'radar-ai' || tab === 'watch-legacy' || tab === 'watch') {
    return JOB_RADAR_ENABLED ? 'watch' : 'explore';
  }
  return tab as TabType;
}

type ExploreViewType = ExploreViewId;
type HireViewType = 'search' | 'interested';

const Home2: React.FC = () => {
  const { t } = useTranslation(['common', 'jobRadarAi', 'watch']);
  const { isViewingAsRecruiter, accountMode } = useViewMode();
  const { userProfile } = useUserProfile();
  const isRecruiterAccount = accountMode === 'recruiter';
  // Paused via JOB_RADAR_ENABLED — tab + unseen poll stay off so we don't
  // hit the scaled-to-zero job-watcher. Revive by flipping the flag.
  const radarEnabled = JOB_RADAR_ENABLED;
  const watchApi = useWatchApi();
  const [watchUnseen, setWatchUnseen] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize tab from URL, or default based on view mode (recruiter -> hire)
  const initialTab = normalizeExplorerTab(searchParams.get('tab'))
    || (isViewingAsRecruiter ? 'hire' : 'explore');
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  
  // Initialize explore view from URL or default to 'cvs'
  const initialView = normalizeExploreView(searchParams.get('view'));
  const [exploreView, setExploreView] = useState<ExploreViewType>(initialView);
  const initialHireView = (initialTab === 'hire' && searchParams.get('view') === 'search') ? 'search' : 'interested';
  const [hireView, setHireView] = useState<HireViewType>(initialHireView);
  
  // Track whether state change came from URL sync (browser back/forward)
  const fromUrlSync = useRef(false);
  const isFirstRender = useRef(true);

  // Keep hire ↔ explore home tabs aligned with the header view toggle. Catches
  // desync when viewMode is already explorer but ?tab=hire / activeTab stuck.
  useEffect(() => {
    const tabForView: TabType = isViewingAsRecruiter ? 'hire' : 'explore';
    const isShellHomeTab = activeTab === 'hire' || activeTab === 'explore';
    if (!isShellHomeTab || activeTab === tabForView) return;
    fromUrlSync.current = true;
    setActiveTab(tabForView);
  }, [isViewingAsRecruiter, activeTab]);

  // Update URL when tab or view changes (preserve extra params like ?talk=)
  useEffect(() => {
    const replace = isFirstRender.current || fromUrlSync.current;
    fromUrlSync.current = false;
    isFirstRender.current = false;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      if (activeTab === 'explore' && exploreView !== 'cvs') {
        next.set('view', exploreView);
      } else if (activeTab === 'hire' && hireView !== 'interested') {
        next.set('view', hireView);
      } else {
        next.delete('view');
      }
      return next;
    }, { replace });
  }, [activeTab, exploreView, hireView, setSearchParams]);
  
  // Sync URL → state when navigated externally (e.g. browser back/forward, nudge buttons)
  useEffect(() => {
    const tabFromUrl = normalizeExplorerTab(searchParams.get('tab'));
    const viewFromUrl = searchParams.get('view');
    let changed = false;
    if (tabFromUrl && tabFromUrl !== activeTab) { setActiveTab(tabFromUrl); changed = true; }
    if (activeTab === 'explore' && viewFromUrl) {
      const safeView = normalizeExploreView(viewFromUrl);
      if (safeView !== exploreView) { setExploreView(safeView); changed = true; }
    }
    if (activeTab === 'explore' && !viewFromUrl && exploreView !== 'cvs') { setExploreView('cvs'); changed = true; }
    if (activeTab === 'hire' && viewFromUrl === 'search' && hireView !== 'search') { setHireView('search'); changed = true; }
    if (activeTab === 'hire' && !viewFromUrl && hireView !== 'interested') { setHireView('interested'); changed = true; }
    if (changed) fromUrlSync.current = true;
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'explore') setExploreView('cvs');
  }, [activeTab]);

  const tabs = [
    {
      id: 'explore' as TabType,
      title: 'Explore',
      emoji: '🔍',
      Icon: Compass,
      underlineGradient: 'from-pink-400 via-pink-500 to-pink-600',
      component: CVSearch
    },
    {
      // Admin-only during rollout (gated via explorerTabIds below).
      id: 'watch' as TabType,
      title: t('jobRadarAi:tab', 'Jobs'),
      emoji: '🤖',
      Icon: Bot,
      underlineGradient: 'from-yellow-400 via-pink-500 to-rose-800',
      component: JobRadarAIView
    },
    {
      id: 'practice' as TabType,
      title: t('navTabs.interviews', 'Interviews'),
      emoji: '🎤',
      Icon: Dumbbell,
      underlineGradient: 'from-rose-400 via-pink-500 to-rose-500',
      component: MockHome
    },
    {
      id: 'passport' as TabType,
      title: 'Passport',
      emoji: '🪪',
      Icon: IdCard,
      underlineGradient: 'from-yellow-300 via-yellow-400 to-yellow-500',
      component: MyPassport
    },
    {
      id: 'inspire' as TabType,
      title: 'Inspire',
      emoji: '✨',
      underlineGradient: 'from-yellow-300 via-yellow-400 to-yellow-500',
      component: CVUpload
    },
    {
      id: 'hire' as TabType,
      title: 'Hire',
      emoji: '🎯',
      underlineGradient: 'from-rose-500 via-rose-600 to-rose-700',
      component: CandidateSearch
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab)!;

  // Explorer shell shows Explore/Practice/Passport; recruiter shell uses the
  // dedicated HiringBoard nav. Inspire stays mounted for direct ?tab=inspire
  // access (and recruiter gating) but is no longer a visible tab.
  const explorerTabIds: TabType[] = [
    'explore',
    ...(radarEnabled ? (['watch'] as TabType[]) : []),
    'practice', 'passport',
  ];
  const visibleTabs = tabs.filter(tab => explorerTabIds.includes(tab.id));

  // Poll the Radar unseen badge at login + on window focus. The endpoint also
  // advances the daily pull+promote server-side (and enables the CV-based "For
  // you" radar by default for CV users), so the daily drop accrues even when the
  // user never opens the Radar tab. Signed-in explorer users only — recruiters
  // get the recruiter gate on this tab, and logged-out calls would 401.
  useEffect(() => {
    if (!radarEnabled || !userProfile || isRecruiterAccount) return;
    // While the Radar tab is open, JobRadarAIView is the single source of truth for
    // the badge (live, via onUnseenChange). Skip this server poll there so its
    // re-promoting count can't overwrite the live value and bounce the badge back
    // up while the user is actively reading. Switching away re-runs this effect
    // (activeTab dep) and reconciles immediately.
    if (activeTab === 'watch') return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { unseen } = await watchApi.unseenCount();
        if (!cancelled) setWatchUnseen(unseen || 0);
      } catch { /* ignore badge poll errors */ }
    };
    void poll();
    const onFocus = () => { void poll(); };
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [radarEnabled, userProfile, isRecruiterAccount, watchApi, activeTab]);

  // While the Radar tab is open, JobRadarAIView owns the badge via onUnseenChange:
  // it reports the live total unread (auto + role radars) and decrements it the
  // instant a thread is read, so the badge ticks down in real time at every level
  // (tab + per-radar) without waiting for the next focus poll.

  // ?tab=watch / radar-ai bookmarks fall back to Explore while JOB_RADAR_ENABLED
  // is false (see normalizeExplorerTab).

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">

      {/* Tab Navigation - explorer shell only; recruiter shell uses HiringBoard's own nav */}
      {!isViewingAsRecruiter && (
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex w-full sm:w-auto">
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      relative flex-1 sm:flex-none px-1 sm:px-10 md:px-14 py-2.5 sm:py-3.5 font-bold text-[11px] sm:text-[15px] md:text-base transition-all duration-300
                      flex items-center justify-center gap-1 sm:gap-2.5 min-w-0 sm:min-w-[140px] md:min-w-[180px]
                      ${isActive 
                        ? 'text-gray-700'
                        : 'text-gray-500 hover:text-gray-700'
                      }
                    `}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-1.5 sm:gap-2 min-w-0"
                    >
                      {(tab as any).Icon ? (
                        <span className="flex-shrink-0">
                          {React.createElement((tab as any).Icon, { className: 'w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]' })}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-lg flex-shrink-0 leading-none">{tab.emoji}</span>
                      )}
                      <span className="text-[13px] sm:text-[15px] md:text-base truncate">{tab.title}</span>
                      {/* Inline pill on desktop (looks natural beside the
                          label and matches the original layout). */}
                      {(tab as any).isBeta && (
                        <span className="hidden sm:inline ml-1 text-[9px] uppercase tracking-wider font-bold text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full leading-none">
                          Beta
                        </span>
                      )}
                      {/* Mirrors Vee's chat bubbles (white + pink border, with
                          the squared top-left corner pointing left like the tip
                          next to her avatar) so it reads as a tiny radar ping. */}
                      {tab.id === 'watch' && watchUnseen > 0 && !isActive && (
                        <span className="hidden sm:inline-flex ml-1.5 items-center justify-center text-[10px] font-bold text-pink-600 bg-white border border-pink-200 px-1.5 py-[3px] rounded-lg rounded-tl-sm leading-none min-w-[20px] shadow-sm">
                          +{watchUnseen > 99 ? '99' : watchUnseen}
                        </span>
                      )}
                    </motion.div>
                    {/* Tiny corner badge ONLY on mobile, tucked top-right
                        outside the text flow so "Practice" gets the full
                        tab width on phones. Same rounded-pill shape as
                        the desktop badge, just smaller. */}
                    {(tab as any).isBeta && (
                      <span className="sm:hidden absolute top-0.5 right-0.5 text-[6px] uppercase tracking-wide font-bold text-rose-600 bg-rose-50 border border-rose-200 px-1 py-px rounded-full leading-none pointer-events-none">
                        Beta
                      </span>
                    )}
                    {tab.id === 'watch' && watchUnseen > 0 && !isActive && (
                      <span className="sm:hidden absolute top-0.5 right-0.5 inline-flex items-center justify-center text-[8px] font-bold text-pink-600 bg-white border border-pink-200 px-1 py-px rounded-md rounded-tl-[2px] leading-none pointer-events-none min-w-[14px] shadow-sm">
                        +{watchUnseen > 9 ? '9' : watchUnseen}
                      </span>
                    )}
                    
                    {isActive && (
                      <motion.div
                        className={`absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r ${tab.underlineGradient} rounded-t-full`}
                        layoutId="activeTabIndicator"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
      )}

      {/* Explore world navigation — Passports / Community / Creators (hidden when only one world is enabled). */}
      {!isViewingAsRecruiter && activeTab === 'explore' && getVisibleExploreViews().length > 1 && (
        <div className={`flex justify-center px-4 pt-2.5 sm:pt-4 ${
          exploreView === 'community' ? 'bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100'
          : exploreView === 'creators' ? 'bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200'
          : ''
        }`}>
          <div className="inline-flex items-center gap-0.5 rounded-full bg-slate-100/90 p-0.5 ring-1 ring-slate-200/60 max-w-full overflow-x-auto">
            {([
              { id: 'cvs', label: t('explore.worlds.passports') },
              { id: 'community', label: t('explore.worlds.community') },
              { id: 'creators', label: t('explore.worlds.creators') },
            ] as { id: ExploreViewType; label: string }[])
              .filter((world) => getVisibleExploreViews().includes(world.id))
              .map((world) => {
              const isActive = exploreView === world.id;
              return (
                <button
                  key={world.id}
                  onClick={() => setExploreView(world.id)}
                  aria-pressed={isActive}
                  className={`relative rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-semibold whitespace-nowrap transition-all ${
                    isActive
                      ? 'text-slate-800'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="exploreWorldIndicator"
                      className="absolute inset-0 rounded-full bg-white shadow-sm"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{world.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1">
        {/* CVSearch stays mounted while on explore tab to preserve search results */}
        <div className={activeTab === 'explore' && exploreView === 'cvs' ? 'h-full' : 'hidden'}>
          <CVSearch
            initialCV={searchParams.get('cv')}
          />
        </div>

        {/* CandidateSearch stays mounted (hidden on other tabs / Interested feed) - keeps search results + CV viewer */}
        <div className={activeTab === 'hire' && hireView === 'search' ? 'h-full' : 'hidden'}>
          <CandidateSearch onSwitchToInterested={() => setHireView('interested')} />
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'explore' && EXPLORE_COMMUNITY_ENABLED && exploreView === 'community' && (
            <motion.div
              key="explore-community"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <Community
                embedded={true}
                initialSearch={searchParams.get('search')}
                initialCreate={searchParams.get('create') === 'true'}
                backToCvRef={searchParams.get('back')}
              />
            </motion.div>
          )}
          {activeTab === 'explore' && EXPLORE_CREATORS_ENABLED && exploreView === 'creators' && (
            <motion.div
              key="explore-creators"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <Creators
                isDemo={false}
                embedded={true}
              />
            </motion.div>
          )}
          {activeTab === 'hire' && hireView === 'interested' && (
            <motion.div
              key="hire-board"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <HiringBoard />
            </motion.div>
          )}
          {activeTab === 'passport' && (
            <motion.div
              key="passport"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {isRecruiterAccount ? <RecruiterExplorerGate /> : <MyPassport />}
            </motion.div>
          )}
          {activeTab === 'practice' && (
            <motion.div
              key="practice"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <MockHome embedded />
            </motion.div>
          )}
          {activeTab === 'watch' && radarEnabled && (
            <motion.div
              key="watch"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {isRecruiterAccount ? <RecruiterExplorerGate /> : <JobRadarAIView onUnseenChange={setWatchUnseen} />}
            </motion.div>
          )}
          {activeTab === 'inspire' && (
            <motion.div
              key="inspire"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {isRecruiterAccount ? <RecruiterExplorerGate /> : <CVUpload />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
};

export default Home2;
